// ─── SiteEquipmentsView — gestão de Equipamentos (Petrotec Angola) ────────
//
// Terceiro nível do drilldown: FinalCustomer → CustomerSite → SiteEquipment.
// Acedido via CustomerSitesView → botão "Equipamentos" num posto.
//
// Filtros por categoria via chips clicáveis no topo da lista. Categorias
// vêm do backend (validação) — qualquer extensão futura faz-se em ambos
// os lados sem migração de schema (campo livre).

import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useFormCardScroll } from '../../utils/manageHelpers';
import {
  Box, Typography, Button, TextField, IconButton, Tooltip, Chip, Switch,
  FormControlLabel, CircularProgress, Select, MenuItem, FormControl, InputLabel,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import BuildIcon from '@material-ui/icons/Build';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import AcUnitIcon from '@material-ui/icons/AcUnit';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import ImageIcon from '@material-ui/icons/Image';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { useDispatch, useSelector } from 'react-redux';
import { selectUI, setActiveView, setSelectedCustomerSite, setSelectedFinalCustomer } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';
import ConfirmDialog from '../common/ConfirmDialog';

// Categorias: chave técnica (BD) + label PT/EN + ícone + cor de destaque.
// Mantém-se em sintonia com VALID_CATEGORIES em site-equipments.routes.js.
const CATEGORIES = [
  { key: 'pista',   label: { pt: 'Pista',   en: 'Forecourt' }, Icon: LocalGasStationIcon, color: '#dc2626' },
  { key: 'avac',    label: { pt: 'AVAC',    en: 'HVAC' },      Icon: AcUnitIcon,          color: '#0ea5e9' },
  { key: 'energia', label: { pt: 'Energia', en: 'Power' },     Icon: FlashOnIcon,         color: '#f59e0b' },
  { key: 'imagem',  label: { pt: 'Imagem',  en: 'Branding' },  Icon: ImageIcon,           color: '#a855f7' },
  { key: 'outros',  label: { pt: 'Outros',  en: 'Other' },     Icon: MoreHorizIcon,       color: '#64748b' },
];

const CAT_META = CATEGORIES.reduce((acc, c) => { acc[c.key] = c; return acc; }, {});

// Subtipos de bombas conforme rodapé do BM Petrotec oficial.
// Quando a categoria é 'pista', o operador pode escolher destas opções
// ou continuar a escrever livremente (textfield aceita ambos).
const PUMP_SUBTYPES = [
  { key: 'BEM', label: { pt: 'BEM — Bomba Eletrónica Multiproduto', en: 'BEM — Electronic Multi-product Pump' }, hasProducts: true },
  { key: 'BMS', label: { pt: 'BMS — Bomba Mecânica Simples',       en: 'BMS — Simple Mechanical Pump' }, hasProducts: true },
  { key: 'BES', label: { pt: 'BES — Bomba Electrónica Simples',    en: 'BES — Simple Electronic Pump' }, hasProducts: true },
  { key: 'BMD', label: { pt: 'BMD — Bomba Mecânica Dupla',         en: 'BMD — Double Mechanical Pump' }, hasProducts: true },
  { key: 'BED', label: { pt: 'BED — Bomba Electrónica Dupla',      en: 'BED — Double Electronic Pump' }, hasProducts: true },
  { key: 'MIS', label: { pt: 'MIS — Misturadora',                  en: 'MIS — Mixer' }, hasProducts: true },
  // Subtipos sem bomba/mangueira — produtos não se aplicam
  { key: 'Tubagem',     label: { pt: 'Tubagem',     en: 'Piping' } },
  { key: 'Reservatório', label: { pt: 'Reservatório', en: 'Tank' } },
  { key: 'Pistola',     label: { pt: 'Pistola de abastecimento', en: 'Nozzle' } },
  { key: 'Ar e água',   label: { pt: 'Ar e água',   en: 'Air & water' } },
  { key: 'Outro',       label: { pt: 'Outro (livre)', en: 'Other (free text)' } },
];

// Tipos de produto por mangueira (Petrotec AO 2026-06-21).
const PRODUCT_TYPES = [
  { key: 'diesel',   label: { pt: 'Gasóleo',  en: 'Diesel' } },
  { key: 'gasoline', label: { pt: 'Gasolina', en: 'Gasoline' } },
  { key: 'other',    label: { pt: 'Outro',    en: 'Other' } },
];

function productLabel(p, pt) {
  if (!p?.type) return '—';
  if (p.type === 'other') return p.name ? `Outro: ${p.name}` : 'Outro';
  const def = PRODUCT_TYPES.find(t => t.key === p.type);
  return def ? (pt ? def.label.pt : def.label.en) : p.type;
}

const useStyles = makeStyles(() => ({
  root: {
    width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
    background: '#f8fafc', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
  },
  titleBox: { flex: 1, minWidth: 0 },
  breadcrumb: {
    fontSize: '0.68rem', color: '#64748b', fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap',
  },
  crumbLink: { cursor: 'pointer', color: '#dc2626', '&:hover': { textDecoration: 'underline' } },
  title: {
    fontSize: '1.05rem', fontWeight: 800, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  toolbar: {
    display: 'flex', gap: 8, padding: '12px 24px', alignItems: 'center',
    borderBottom: '1px solid #f1f5f9', background: '#fff', flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1, maxWidth: 360,
    '& .MuiOutlinedInput-root': { borderRadius: 8, background: '#fff', fontSize: '0.78rem' },
  },
  addButton: {
    background: '#3b82f6',
    color: '#fff', borderRadius: 8, textTransform: 'none', fontWeight: 700,
    padding: '6px 16px', fontSize: '0.74rem', boxShadow: 'none',
    '&:hover': { background: '#2563eb', boxShadow: '0 2px 6px rgba(59,130,246,0.25)' },
  },
  saveButton: {
    background: '#3b82f6', color: '#fff',
    textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
    padding: '6px 18px', boxShadow: 'none',
    '&:hover': { background: '#2563eb' },
  },
  catBar: {
    display: 'flex', gap: 6, padding: '10px 24px', alignItems: 'center',
    borderBottom: '1px solid #f1f5f9', background: '#fff', flexWrap: 'wrap',
  },
  catChip: { fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer' },
  list: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  card: {
    background: '#fff', borderRadius: 12, padding: '14px 18px', marginBottom: 10,
    border: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'center', gap: 14,
    transition: 'all 0.2s',
    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' },
  },
  cardInactive: { opacity: 0.55, background: '#f8fafc' },
  cardIcon: {
    width: 40, height: 40, borderRadius: 10, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
  cardMain: { flex: 1, minWidth: 0 },
  cardName: {
    fontSize: '0.92rem', fontWeight: 700, color: '#0f172a',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  cardMeta: {
    fontSize: '0.7rem', color: '#64748b', marginTop: 4,
    display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
  },
  cardActions: { display: 'flex', gap: 4, alignItems: 'center' },
  empty: {
    padding: '80px 24px', textAlign: 'center', color: '#94a3b8',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  },
  formCard: {
    background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  formTitle: {
    fontSize: '0.86rem', fontWeight: 700, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  formRow2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  formRow3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 },
  formActions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
}));

const EMPTY_FORM = {
  siteId: '', // só usado em modo "lista geral"
  category: 'pista', subtype: '', positionLabel: '',
  brand: '', model: '', serialNumber: '', assetNumber: '', product: '',
  // Petrotec AO 2026-06-21 — até 3 produtos por bomba (1 por mangueira).
  // Cada slot: { type: 'diesel'|'gasoline'|'other'|'', name?: string }.
  // Vazios são filtrados ao gravar.
  products: [{ type: '', name: '' }, { type: '', name: '' }, { type: '', name: '' }],
  installedAt: '',
  photos: [], // array de data-URLs (base64) ou URLs externos
};

function SiteEquipmentsView({ lang = 'pt' }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const { selectedCustomerSite } = useSelector(selectUI);
  const pt = lang === 'pt';

  const [items, setItems] = useState([]);
  const [allSites, setAllSites] = useState([]); // só em modo lista geral
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null); // null = todas
  const [filterSite, setFilterSite] = useState(''); // filtro de posto (lista geral)
  const [editingId, setEditingId] = useState(null);
  const formCardRef = useFormCardScroll(editingId);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Modo: 'drilldown' (posto fixado) vs 'list' (todos os equipamentos do tenant)
  const siteId = selectedCustomerSite?.id;
  const siteName = selectedCustomerSite?.name || '';
  const fcId = selectedCustomerSite?.finalCustomerId;
  const fcName = selectedCustomerSite?.finalCustomerName || '';
  const mode = siteId ? 'drilldown' : 'list';

  // Em modo lista geral, carregar todos os postos para o filtro/form
  useEffect(() => {
    if (mode !== 'list' || !token) return;
    fetch('/planner-api/customer-sites', { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setAllSites(Array.isArray(data) ? data : []))
      .catch(() => setAllSites([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, token]);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (mode === 'drilldown') params.set('siteId', String(siteId));
      else if (filterSite) params.set('siteId', String(filterSite));
      if (showInactive) params.set('includeInactive', '1');
      const qs = params.toString();
      const r = await fetch(`/planner-api/site-equipments${qs ? `?${qs}` : ''}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('SiteEquipments fetch error:', e);
      setError(pt ? 'Não foi possível carregar a lista.' : 'Could not load list.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, mode, siteId, filterSite, showInactive, pt]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Contagem por categoria (para os chips)
  const catCounts = useMemo(() => {
    const counts = {};
    items.forEach(it => { counts[it.category] = (counts[it.category] || 0) + 1; });
    return counts;
  }, [items]);

  function startCreate() {
    setForm({
      ...EMPTY_FORM,
      siteId: mode === 'drilldown' ? siteId : (filterSite || ''),
    });
    setEditingId('new');
  }

  function startEdit(eq) {
    setForm({
      siteId: eq.siteId,
      category: eq.category || 'pista',
      subtype: eq.subtype || '',
      positionLabel: eq.positionLabel || '',
      brand: eq.brand || '',
      model: eq.model || '',
      serialNumber: eq.serialNumber || '',
      assetNumber: eq.assetNumber || '',
      product: eq.product || '',
      products: (() => {
        const arr = Array.isArray(eq.products) ? eq.products : [];
        const slots = [{ type: '', name: '' }, { type: '', name: '' }, { type: '', name: '' }];
        for (let i = 0; i < Math.min(arr.length, 3); i++) {
          slots[i] = { type: arr[i]?.type || '', name: arr[i]?.name || '' };
        }
        return slots;
      })(),
      installedAt: eq.installedAt ? String(eq.installedAt).slice(0, 10) : '',
      photos: Array.isArray(eq.photos) ? eq.photos : [],
    });
    setEditingId(eq.id);
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  async function saveForm() {
    if (!form.subtype.trim()) {
      setError(pt ? 'Subtipo obrigatório (ex.: Bomba, Tubagem, Gerador).' : 'Subtype required.');
      return;
    }
    if (!form.siteId) {
      setError(pt ? 'Posto obrigatório.' : 'Site required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = JSON.stringify({
        siteId: form.siteId,
        category: form.category,
        subtype: form.subtype.trim(),
        positionLabel: form.positionLabel.trim() || null,
        brand: form.brand.trim() || null,
        model: form.model.trim() || null,
        serialNumber: form.serialNumber.trim() || null,
        assetNumber: form.assetNumber.trim() || null,
        product: form.product.trim() || null,
        products: (() => {
          const out = [];
          for (const p of (form.products || []).slice(0, 3)) {
            if (!p?.type) continue;
            const entry = { type: p.type };
            if (p.type === 'other' && p.name?.trim()) entry.name = p.name.trim().slice(0, 50);
            out.push(entry);
          }
          return out;
        })(),
        installedAt: form.installedAt || null,
        photos: Array.isArray(form.photos) ? form.photos : [],
      });
      const isNew = editingId === 'new';
      const url = isNew
        ? '/planner-api/site-equipments'
        : `/planner-api/site-equipments/${editingId}`;
      const method = isNew ? 'POST' : 'PUT';
      const r = await fetch(url, { method, headers, body });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
      await fetchAll();
    } catch (e) {
      setError(e.message || (pt ? 'Erro ao guardar.' : 'Save error.'));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(eq) {
    try {
      const r = await fetch(`/planner-api/site-equipments/${eq.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ isActive: !eq.isActive }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchAll();
    } catch (e) {
      setError(e.message);
    }
  }

  async function confirmDeleteNow() {
    if (!deleteConfirm) return;
    try {
      const r = await fetch(`/planner-api/site-equipments/${deleteConfirm.id}`, {
        method: 'DELETE', headers,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDeleteConfirm(null);
      await fetchAll();
    } catch (e) {
      setError(e.message);
      setDeleteConfirm(null);
    }
  }

  function goBackToSites() {
    dispatch(setSelectedCustomerSite(null));
    dispatch(setActiveView('customerSites'));
  }
  function goBackToCustomers() {
    dispatch(setSelectedCustomerSite(null));
    dispatch(setSelectedFinalCustomer(null));
    dispatch(setActiveView('finalCustomers'));
  }

  const filtered = items.filter(it => {
    if (activeCategory && it.category !== activeCategory) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [it.subtype, it.positionLabel, it.brand, it.model, it.serialNumber, it.assetNumber, it.product]
      .some(v => v && String(v).toLowerCase().includes(q));
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Tooltip title={mode === 'drilldown'
          ? (pt ? 'Voltar a Postos' : 'Back to Sites')
          : (pt ? 'Voltar a Manutenção' : 'Back to Maintenance')}>
          <IconButton
            size="small"
            onClick={mode === 'drilldown' ? goBackToSites : () => dispatch(setActiveView('maintenance'))}
          >
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Box className={classes.titleBox}>
          <Box className={classes.breadcrumb}>
            <span className={classes.crumbLink} onClick={() => dispatch(setActiveView('maintenance'))}>
              {pt ? 'Manutenção' : 'Maintenance'}
            </span>
            {mode === 'drilldown' && (
              <>
                <span>›</span>
                <span className={classes.crumbLink} onClick={goBackToCustomers}>
                  {pt ? 'Clientes Finais' : 'Final Customers'}
                </span>
                <span>›</span>
                <span className={classes.crumbLink} onClick={goBackToSites}>{fcName}</span>
                <span>›</span>
                <span style={{ fontWeight: 700, color: '#0f172a' }}>{siteName}</span>
              </>
            )}
            {mode === 'list' && (
              <>
                <span>›</span>
                <span style={{ fontWeight: 700, color: '#0f172a' }}>{pt ? 'Equipamentos' : 'Equipments'}</span>
              </>
            )}
          </Box>
          <Typography className={classes.title}>
            <BuildIcon style={{ fontSize: 20, color: '#f59e0b' }} />
            {pt ? 'Equipamentos' : 'Equipments'}
          </Typography>
        </Box>
        <Tooltip title={pt ? 'Recarregar' : 'Refresh'}>
          <IconButton size="small" onClick={fetchAll} disabled={loading}>
            <RefreshIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className={classes.toolbar}>
        <TextField
          className={classes.searchInput}
          size="small" variant="outlined"
          placeholder={pt ? 'Procurar por subtipo, posição, série…' : 'Search by subtype, position, serial…'}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {mode === 'list' && (
          <FormControl size="small" variant="outlined" style={{ minWidth: 220 }}>
            <InputLabel>{pt ? 'Posto' : 'Site'}</InputLabel>
            <Select
              label={pt ? 'Posto' : 'Site'}
              value={filterSite}
              onChange={e => setFilterSite(e.target.value)}
            >
              <MenuItem value="">{pt ? 'Todos' : 'All'}</MenuItem>
              {allSites.filter(s => s.isActive).map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.finalCustomerName ? `${s.finalCustomerName} · ${s.name}` : s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
            />
          }
          label={
            <Typography style={{ fontSize: '0.75rem', color: '#475569' }}>
              {pt ? 'Mostrar desactivados' : 'Show inactive'}
            </Typography>
          }
        />
        <Box style={{ flex: 1 }} />
        <Button
          className={classes.addButton}
          startIcon={<AddIcon style={{ fontSize: 18 }} />}
          onClick={startCreate}
          disabled={editingId !== null}
        >
          {pt ? 'Novo equipamento' : 'New equipment'}
        </Button>
      </Box>

      <Box className={classes.catBar}>
        <Chip
          label={`${pt ? 'Todas' : 'All'} (${items.length})`}
          size="small"
          className={classes.catChip}
          onClick={() => setActiveCategory(null)}
          style={{
            background: activeCategory === null ? '#0f172a' : '#e2e8f0',
            color: activeCategory === null ? '#fff' : '#475569',
          }}
        />
        {CATEGORIES.map(cat => {
          const count = catCounts[cat.key] || 0;
          const active = activeCategory === cat.key;
          const CatIcon = cat.Icon;
          return (
            <Chip
              key={cat.key}
              label={`${cat.label[pt ? 'pt' : 'en']} (${count})`}
              size="small"
              className={classes.catChip}
              icon={<CatIcon style={{ fontSize: 14, color: active ? '#fff' : cat.color }} />}
              onClick={() => setActiveCategory(active ? null : cat.key)}
              style={{
                background: active ? cat.color : `${cat.color}1A`,
                color: active ? '#fff' : cat.color,
              }}
            />
          );
        })}
      </Box>

      <Box className={classes.list}>
        {error && (
          <Box style={{
            padding: '10px 14px', background: '#fef2f2',
            border: '1px solid #fecaca', color: '#991b1b',
            borderRadius: 8, marginBottom: 12, fontSize: '0.78rem',
          }}>
            {error}
          </Box>
        )}

        {editingId !== null && (
          <Box ref={formCardRef} className={classes.formCard}>
            <Typography className={classes.formTitle}>
              <BuildIcon style={{ fontSize: 18, color: '#f59e0b' }} />
              {editingId === 'new'
                ? (pt
                    ? (mode === 'drilldown' ? `Novo equipamento em ${siteName}` : 'Novo equipamento')
                    : (mode === 'drilldown' ? `New equipment in ${siteName}` : 'New equipment'))
                : (pt ? 'Editar equipamento' : 'Edit equipment')}
            </Typography>
            {/* Picker de posto só fora do drilldown */}
            {mode === 'list' && (
              <FormControl size="small" variant="outlined" disabled={editingId !== 'new'}>
                <InputLabel>{pt ? 'Posto *' : 'Site *'}</InputLabel>
                <Select
                  label={pt ? 'Posto *' : 'Site *'}
                  value={form.siteId || ''}
                  onChange={e => setForm({ ...form, siteId: e.target.value })}
                >
                  {allSites.filter(s => s.isActive).map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.finalCustomerName ? `${s.finalCustomerName} · ${s.name}` : s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box className={classes.formRow3}>
              <FormControl size="small" variant="outlined">
                <InputLabel>{pt ? 'Categoria *' : 'Category *'}</InputLabel>
                <Select
                  label={pt ? 'Categoria *' : 'Category *'}
                  value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                >
                  {CATEGORIES.map(cat => (
                    <MenuItem key={cat.key} value={cat.key}>
                      {cat.label[pt ? 'pt' : 'en']}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Subtipo: Autocomplete com lista controlada para 'pista'
                  (BEM/BMS/BES/BMD/BED/MIS conforme rodapé do BM Petrotec)
                  + opção "Outro (livre)". Para outras categorias é texto
                  livre (operador escreve o que quiser). `freeSolo` permite
                  manter input que não esteja na lista. */}
              <Autocomplete
                freeSolo
                size="small"
                options={form.category === 'pista'
                  ? PUMP_SUBTYPES.map(s => s.key)
                  : []}
                value={form.subtype || ''}
                onChange={(_, v) => setForm({ ...form, subtype: v || '' })}
                onInputChange={(_, v) => setForm({ ...form, subtype: v || '' })}
                getOptionLabel={(opt) => {
                  const meta = PUMP_SUBTYPES.find(s => s.key === opt);
                  return meta ? meta.label[pt ? 'pt' : 'en'] : (opt || '');
                }}
                renderInput={(params) => (
                  <TextField {...params}
                    label={pt ? 'Subtipo *' : 'Subtype *'}
                    variant="outlined"
                    placeholder={form.category === 'pista'
                      ? (pt ? 'BEM, BMS, Tubagem…' : 'BEM, BMS, Piping…')
                      : (pt ? 'Bomba, Gerador, AC…' : 'Pump, Generator…')}
                    autoFocus
                  />
                )}
              />
              <TextField
                label={pt ? 'Posição / Etiqueta' : 'Position / Label'}
                size="small" variant="outlined"
                value={form.positionLabel}
                onChange={e => setForm({ ...form, positionLabel: e.target.value })}
                placeholder={pt ? 'Bomba 1, Lado A…' : 'Pump 1, Side A…'}
              />
            </Box>
            <Box className={classes.formRow2}>
              <TextField
                label={pt ? 'Marca' : 'Brand'}
                size="small" variant="outlined"
                value={form.brand}
                onChange={e => setForm({ ...form, brand: e.target.value })}
              />
              <TextField
                label={pt ? 'Modelo' : 'Model'}
                size="small" variant="outlined"
                value={form.model}
                onChange={e => setForm({ ...form, model: e.target.value })}
              />
            </Box>
            <Box className={classes.formRow2}>
              <TextField
                label={pt ? 'Nº Fabrico (série)' : 'Serial number'}
                size="small" variant="outlined"
                value={form.serialNumber}
                onChange={e => setForm({ ...form, serialNumber: e.target.value })}
              />
              <TextField
                label={pt ? 'Nº Petrotec (interno)' : 'Asset number'}
                size="small" variant="outlined"
                value={form.assetNumber}
                onChange={e => setForm({ ...form, assetNumber: e.target.value })}
              />
            </Box>
            <Box className={classes.formRow2}>
              <TextField
                label={pt ? 'Data de instalação' : 'Installed at'}
                size="small" variant="outlined" type="date"
                value={form.installedAt}
                onChange={e => setForm({ ...form, installedAt: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <Box />
            </Box>

            {/* Produtos por mangueira — até 3. Só aparece para subtipos de
                bomba (BEM/BMS/BES/BMD/BED/MIS) e para subtipos livres não
                listados (caso o operador escreva à mão). Tubagem,
                Reservatório, Pistola e "Ar e água" não pedem produto. */}
            {(() => {
              const sub = PUMP_SUBTYPES.find(s => s.key === form.subtype);
              const noProducts = sub && !sub.hasProducts;
              if (noProducts) return null;
              return (
                <Box style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', marginBottom: 8 }}>
                    {pt ? 'Produtos por mangueira (até 3)' : 'Products per nozzle (max 3)'}
                  </Typography>
                  {[0, 1, 2].map(idx => {
                    const slot = form.products?.[idx] || { type: '', name: '' };
                    return (
                      <Box key={idx} style={{ display: 'grid', gridTemplateColumns: '70px 1fr 1.2fr', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                        <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b' }}>
                          {pt ? `Produto ${idx + 1}` : `Product ${idx + 1}`}
                        </Typography>
                        <FormControl size="small" variant="outlined">
                          <InputLabel>{pt ? 'Tipo' : 'Type'}</InputLabel>
                          <Select
                            label={pt ? 'Tipo' : 'Type'}
                            value={slot.type}
                            onChange={e => {
                              const next = [...(form.products || [])];
                              next[idx] = { ...slot, type: e.target.value, name: e.target.value === 'other' ? slot.name : '' };
                              setForm({ ...form, products: next });
                            }}
                          >
                            <MenuItem value=""><em>— {pt ? 'Nenhum' : 'None'} —</em></MenuItem>
                            {PRODUCT_TYPES.map(p => (
                              <MenuItem key={p.key} value={p.key}>{pt ? p.label.pt : p.label.en}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {slot.type === 'other' ? (
                          <TextField
                            label={pt ? 'Nome (ex: GPL)' : 'Name (e.g. LPG)'}
                            size="small" variant="outlined"
                            value={slot.name || ''}
                            onChange={e => {
                              const next = [...(form.products || [])];
                              next[idx] = { ...slot, name: e.target.value };
                              setForm({ ...form, products: next });
                            }}
                            inputProps={{ maxLength: 50 }}
                          />
                        ) : (<Box />)}
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}

            {/* Fotos do equipamento — campo do BM Petrotec. Operador
                escolhe N fotos (max 10), elas vão em base64 directamente
                na coluna JSON. Em produção podíamos passar para S3, mas
                base64 é suficiente para começar — média 200-500KB cada. */}
            <Box>
              <Typography style={{
                fontSize: '0.74rem', fontWeight: 700, color: '#475569',
                marginBottom: 8,
              }}>
                {pt ? 'Fotos do equipamento' : 'Equipment photos'}
                <span style={{ fontSize: '0.62rem', fontWeight: 500, color: '#94a3b8', marginLeft: 6 }}>
                  ({(form.photos || []).length}/10)
                </span>
              </Typography>
              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                {(form.photos || []).map((p, idx) => (
                  <Box key={idx} style={{
                    position: 'relative', width: 80, height: 80,
                    borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0',
                  }}>
                    <img src={p} alt={`Foto ${idx + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <IconButton size="small"
                      onClick={() => setForm({ ...form, photos: form.photos.filter((_, i) => i !== idx) })}
                      style={{
                        position: 'absolute', top: 2, right: 2,
                        background: 'rgba(220,38,38,0.9)', color: '#fff',
                        width: 20, height: 20, padding: 0,
                      }}>
                      <CloseIcon style={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                ))}
                {(form.photos || []).length < 10 && (
                  <Box component="label" style={{
                    width: 80, height: 80, borderRadius: 8,
                    border: '2px dashed #cbd5e1', background: '#f8fafc',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: '#64748b', fontSize: '0.62rem', fontWeight: 600,
                  }}>
                    <AddIcon style={{ fontSize: 22, color: '#94a3b8' }} />
                    {pt ? 'Adicionar' : 'Add'}
                    <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        const remaining = 10 - (form.photos || []).length;
                        const slice = files.slice(0, remaining);
                        const dataUrls = await Promise.all(slice.map(f => new Promise((resolve) => {
                          const reader = new FileReader();
                          reader.onload = () => resolve(reader.result);
                          reader.readAsDataURL(f);
                        })));
                        setForm({ ...form, photos: [...(form.photos || []), ...dataUrls] });
                        e.target.value = '';
                      }} />
                  </Box>
                )}
              </Box>
            </Box>

            <Box className={classes.formActions}>
              <Button
                onClick={cancelEdit}
                style={{ textTransform: 'none', fontSize: '0.78rem' }}
                disabled={saving}
              >
                {pt ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button
                onClick={saveForm}
                disabled={saving}
                className={classes.saveButton}
                startIcon={saving ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <CheckIcon style={{ fontSize: 16 }} />}
              >
                {pt ? 'Guardar' : 'Save'}
              </Button>
            </Box>
          </Box>
        )}

        {loading && items.length === 0 ? (
          <Box className={classes.empty}>
            <CircularProgress size={32} style={{ color: '#3b82f6' }} />
            <Typography style={{ fontSize: '0.8rem' }}>
              {pt ? 'A carregar…' : 'Loading…'}
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box className={classes.empty}>
            <BuildIcon style={{ fontSize: 48, color: '#cbd5e1' }} />
            <Typography style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {search || activeCategory
                ? (pt ? 'Sem resultados para os filtros aplicados.' : 'No results for current filters.')
                : mode === 'drilldown'
                  ? (pt ? `Sem equipamentos em ${siteName}.` : `No equipments in ${siteName}.`)
                  : (pt ? 'Ainda não há equipamentos.' : 'No equipments yet.')}
            </Typography>
            {!search && !activeCategory && (mode === 'drilldown' || allSites.length > 0) && (
              <Button
                className={classes.addButton}
                startIcon={<AddIcon style={{ fontSize: 18 }} />}
                onClick={startCreate}
                disabled={editingId !== null}
              >
                {pt ? 'Criar primeiro' : 'Create first one'}
              </Button>
            )}
            {mode === 'list' && allSites.length === 0 && (
              <Typography style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                {pt ? 'Cria primeiro um posto.' : 'Create a site first.'}
              </Typography>
            )}
          </Box>
        ) : (
          filtered.map(eq => {
            const cat = CAT_META[eq.category] || CAT_META.outros;
            const CatIcon = cat.Icon;
            const subParts = [eq.brand, eq.model].filter(Boolean).join(' / ');
            return (
              <Box
                key={eq.id}
                className={`${classes.card} ${!eq.isActive ? classes.cardInactive : ''}`}
              >
                <Box className={classes.cardIcon} style={{ background: `${cat.color}1A` }}>
                  <CatIcon style={{ fontSize: 20, color: cat.color }} />
                </Box>
                <Box className={classes.cardMain}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Typography className={classes.cardName}>
                      {eq.subtype}{eq.positionLabel ? ` · ${eq.positionLabel}` : ''}
                    </Typography>
                    {mode === 'list' && (eq.finalCustomerName || eq.siteName) && (
                      <Chip
                        label={`${eq.finalCustomerName || ''}${eq.finalCustomerName && eq.siteName ? ' · ' : ''}${eq.siteName || ''}`}
                        size="small"
                        style={{
                          height: 18, fontSize: '0.6rem', fontWeight: 700,
                          background: '#fee2e2', color: '#b91c1c',
                        }}
                      />
                    )}
                    <Chip
                      label={cat.label[pt ? 'pt' : 'en']}
                      size="small"
                      style={{
                        height: 18, fontSize: '0.6rem', fontWeight: 700,
                        background: `${cat.color}1A`, color: cat.color,
                      }}
                    />
                    {Array.isArray(eq.products) && eq.products.length > 0
                      ? eq.products.map((p, i) => (
                          <Chip
                            key={`p${i}`}
                            label={productLabel(p, pt)}
                            size="small"
                            style={{ height: 18, fontSize: '0.6rem', background: '#fef3c7', color: '#92400e', fontWeight: 700 }}
                          />
                        ))
                      : eq.product && (
                          <Chip
                            label={eq.product}
                            size="small"
                            style={{ height: 18, fontSize: '0.6rem', background: '#fef3c7', color: '#92400e', fontWeight: 700 }}
                          />
                        )}
                    {!eq.isActive && (
                      <Chip
                        label={pt ? 'Desactivado' : 'Inactive'}
                        size="small"
                        style={{ height: 18, fontSize: '0.6rem', background: '#e2e8f0', color: '#475569' }}
                      />
                    )}
                  </Box>
                  <Box className={classes.cardMeta}>
                    {subParts && <span>{subParts}</span>}
                    {eq.serialNumber && (
                      <span style={{ fontFamily: 'monospace' }}>
                        {pt ? 'série' : 'serial'}: {eq.serialNumber}
                      </span>
                    )}
                    {eq.assetNumber && (
                      <span style={{ fontFamily: 'monospace', color: '#dc2626', fontWeight: 600 }}>
                        Nº {eq.assetNumber}
                      </span>
                    )}
                    {eq.installedAt && (
                      <span>
                        {pt ? 'instalado' : 'installed'}: {String(eq.installedAt).slice(0, 10)}
                      </span>
                    )}
                    {eq.interventionCount > 0 && (
                      <span style={{ color: '#7c3aed', fontWeight: 600 }}>
                        {eq.interventionCount} {pt ? 'intervenções' : 'interventions'}
                      </span>
                    )}
                    {Array.isArray(eq.photos) && eq.photos.length > 0 && (
                      <span style={{ color: '#16a34a', fontWeight: 600 }}>
                        📷 {eq.photos.length} {pt ? 'fotos' : 'photos'}
                      </span>
                    )}
                  </Box>
                </Box>
                <Box className={classes.cardActions}>
                  <Tooltip title={eq.isActive ? (pt ? 'Desactivar' : 'Deactivate') : (pt ? 'Reactivar' : 'Reactivate')}>
                    <IconButton size="small" onClick={() => toggleActive(eq)}>
                      {eq.isActive
                        ? <CloseIcon style={{ fontSize: 18, color: '#94a3b8' }} />
                        : <CheckIcon style={{ fontSize: 18, color: '#22c55e' }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={pt ? 'Editar' : 'Edit'}>
                    <IconButton size="small" onClick={() => startEdit(eq)} disabled={editingId !== null}>
                      <EditIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={pt ? 'Eliminar (soft)' : 'Delete (soft)'}>
                    <IconButton size="small" onClick={() => setDeleteConfirm({ id: eq.id, name: `${eq.subtype}${eq.positionLabel ? ` · ${eq.positionLabel}` : ''}` })}>
                      <DeleteIcon style={{ fontSize: 16, color: '#ef4444' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      <ConfirmDialog
        open={!!deleteConfirm}
        title={pt ? 'Desactivar equipamento?' : 'Deactivate equipment?'}
        message={deleteConfirm
          ? (pt
            ? `"${deleteConfirm.name}" passa a desactivado. Intervenções já registadas mantêm a referência. Pode ser reactivado em qualquer momento.`
            : `"${deleteConfirm.name}" will be deactivated. Existing interventions keep the reference. Can be reactivated at any time.`)
          : ''}
        severity="warning"
        confirmLabel={pt ? 'Desactivar' : 'Deactivate'}
        cancelLabel={pt ? 'Cancelar' : 'Cancel'}
        onConfirm={confirmDeleteNow}
        onClose={() => setDeleteConfirm(null)}
      />
    </Box>
  );
}

export default SiteEquipmentsView;
